import React, { useEffect, useRef } from 'react';
import HalfCourtSVG from './CourtCanvas';

const GOLD = '#D4AF37';
const RED = '#E11D48';

function arrowColor(type) {
  if (type === 'pass') return '#60A5FA';
  if (type === 'dribble') return RED;
  if (type === 'screen') return '#10B981';
  return GOLD;
}
function arrowDash(type) {
  if (type === 'pass') return '12 8';
  if (type === 'dribble') return '4 6';
  return 'none';
}

function PlayerToken({ p, V }) {
  const x = (p.position.x / 100) * V;
  const y = (p.position.y / 100) * V;
  const isOff = p.team === 'offense';
  const stroke = isOff ? GOLD : RED;
  const photoUrl = p.photo_url;
  const label = p.label;
  const name = p.player_name;
  return (
    <g transform={`translate(${x},${y})`}>
      <circle r="38" fill="#0A192F" stroke={stroke} strokeWidth="3.5" />
      {photoUrl ? (
        <>
          <defs>
            <clipPath id={`exp-clip-${p.id}`}>
              <circle r="34" />
            </clipPath>
          </defs>
          <image href={photoUrl} x="-34" y="-34" width="68" height="68"
            clipPath={`url(#exp-clip-${p.id})`} preserveAspectRatio="xMidYMid slice" crossOrigin="anonymous" />
          <circle cx="28" cy="-24" r="13" fill={stroke} />
          <text x="28" y="-19" textAnchor="middle" fontFamily="Oswald, sans-serif" fontWeight="700" fontSize="16" fill="#0A192F">{label}</text>
        </>
      ) : (
        <text textAnchor="middle" dy="10" fontFamily="Oswald, sans-serif" fontWeight="700" fontSize="30" fill={stroke}>
          {label}
        </text>
      )}
      {name && isOff && (
        <g>
          <rect x="-58" y="44" width="116" height="22" rx="4" fill="#0A192F" stroke={stroke} strokeWidth="1.5" />
          <text x="0" y="60" textAnchor="middle" fontFamily="IBM Plex Sans, sans-serif" fontSize="13" fontWeight="600" fill="#fff">
            {name.length > 16 ? name.slice(0, 14) + '…' : name}
          </text>
        </g>
      )}
    </g>
  );
}

/**
 * Offscreen renderer for export. Renders one fixed-size frame per step.
 * Calls `onReady(refs)` with the array of DOM nodes (one per step).
 */
export default function PlayExportFrames({ steps = [], onReady }) {
  const refs = useRef([]);
  useEffect(() => {
    if (onReady) onReady(refs.current.filter(Boolean));
  }, [steps, onReady]);

  const V = 1000;
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        left: '-100000px',
        top: 0,
        pointerEvents: 'none',
        opacity: 1,
        zIndex: -1,
      }}
    >
      {steps.map((step, i) => (
        <div
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          style={{ width: 1100, height: 1100, background: '#0A192F', padding: 30 }}
        >
          <HalfCourtSVG className="block w-full h-full">
            <defs>
              <marker id={`exp-arr-move-${i}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill={GOLD} />
              </marker>
              <marker id={`exp-arr-pass-${i}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#60A5FA" />
              </marker>
              <marker id={`exp-arr-dribble-${i}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill={RED} />
              </marker>
              <marker id={`exp-arr-screen-${i}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#10B981" />
              </marker>
            </defs>
            {(step.strokes || []).map((s) => (
              <polyline
                key={s.id}
                points={s.points.map(pt => `${(pt.x / 100) * V},${(pt.y / 100) * V}`).join(' ')}
                fill="none" stroke={s.color || GOLD} strokeWidth={s.width || 3}
                strokeLinecap="round" strokeLinejoin="round"
              />
            ))}
            {/* Player movement paths (rendered like dashed trails) */}
            {Object.entries(step.paths || {}).map(([pid, raw]) => {
              const pts = Array.isArray(raw) ? raw : (Array.isArray(raw?.points) ? raw.points : null);
              if (!pts) return null;
              return (
                <polyline
                  key={`p-${pid}`}
                  points={pts.map(pt => `${(pt.x / 100) * V},${(pt.y / 100) * V}`).join(' ')}
                  fill="none" stroke={GOLD} strokeWidth="5"
                  strokeLinecap="round" strokeLinejoin="round"
                  strokeDasharray="2 12" opacity="0.85"
                />
              );
            })}
            {(step.arrows || []).map((a, idx) => {
              const x1 = (a.from.x / 100) * V, y1 = (a.from.y / 100) * V;
              const x2 = (a.to.x / 100) * V, y2 = (a.to.y / 100) * V;
              const type = a.type || 'move';
              return (
                <line
                  key={idx}
                  x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke={arrowColor(type)} strokeWidth="4" strokeLinecap="round"
                  strokeDasharray={arrowDash(type)}
                  markerEnd={`url(#exp-arr-${type}-${i})`}
                />
              );
            })}
            {(step.players || []).map(p => (
              <PlayerToken key={p.id} p={p} V={V} />
            ))}
          </HalfCourtSVG>
        </div>
      ))}
    </div>
  );
}
